import React, { useContext, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useVoucher from '../../hooks/useVoucher';
import { getStatusLabel } from '../../util/paymentUtils';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';

// Standalone redemption-history view for the "Gutscheine" section.
// Fully gated by the dedicated 'voucher:special:redemptionHistory' right and works for both
// product and value vouchers. Shows the redemption status and every load/unload (Auf- und
// Entladungen) with date and the comment describing what the voucher was redeemed for. Related
// bookings are linked via an openInNew icon that opens the BookingDetailsModal.
// Online redemption of value vouchers is currently disabled; this view is forward-compatible and
// will display such redemptions (incl. their related bookings) automatically once enabled.

const REQUIRED_RIGHT = 'voucher:special:redemptionHistory';

function formatEuro(value) {
  if (value == null) return '-';
  return (
    Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  );
}

function getTransactionTypeLabel(type) {
  switch (type) {
    case 'load':
      return 'Aufladung';
    case 'reload':
      return 'Nachladung';
    case 'redeem':
      return 'Einlösung';
    default:
      return type || '-';
  }
}

const VoucherRedemptionHistory = () => {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight(REQUIRED_RIGHT)) {
    return <Navigate to='/welcome' replace />;
  }

  const { getVoucherRedemptionHistory } = useVoucher();

  const codeInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [searched, setSearched] = useState(false);

  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const handleSearch = async () => {
    const voucherCode = (codeInputRef.current?.value || '').trim();
    if (!voucherCode) {
      toast.error('Bitte einen Gutscheincode eingeben');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const data = await getVoucherRedemptionHistory(voucherCode);
      setVoucher(data);
    } catch (error) {
      setVoucher(null);
      if (error?.code === 'VOUCHER_NOT_FOUND' || error?.statusCode === 404) {
        toast.error('Kein Gutschein mit diesem Code gefunden');
      } else {
        toast.error('Fehler beim Laden der Einlösehistorie');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const openBooking = bookingId => {
    if (!bookingId) return;
    setSelectedBookingId(bookingId);
    setBookingModalOpen(true);
  };

  const transactions = Array.isArray(voucher?.transactionHistory)
    ? voucher.transactionHistory
    : [];
  const isProductVoucher =
    voucher?.isProductVoucher ?? Boolean(voucher?.relatedVoucherProductId);

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        Gutschein-Einlösehistorie
      </Typography>

      <Stack direction='row' spacing={2} sx={{ mb: 3 }} alignItems='center'>
        <TextField
          inputRef={codeInputRef}
          label='Gutscheincode'
          size='small'
          onKeyDown={handleKeyDown}
          sx={{ minWidth: 280 }}
        />
        <Button variant='contained' onClick={handleSearch} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Suchen'}
        </Button>
      </Stack>

      {!loading && voucher && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant='h6'>{voucher.voucherCode}</Typography>
              <Chip
                size='small'
                label={isProductVoucher ? 'Produktgutschein' : 'Wertgutschein'}
                color={isProductVoucher ? 'secondary' : 'primary'}
              />
              <Chip
                size='small'
                variant='outlined'
                label={`Einlösestatus: ${getStatusLabel(voucher.status)}`}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
              }}
            >
              {isProductVoucher && voucher.relatedVoucherProductId?.name && (
                <Typography variant='body2'>
                  <strong>Produkt:</strong> {voucher.relatedVoucherProductId.name}
                </Typography>
              )}
              <Typography variant='body2'>
                <strong>Aktuelles Guthaben:</strong>{' '}
                {formatEuro(voucher.currentBalance?.sum)}
              </Typography>
              <Typography variant='body2'>
                <strong>Ursprüngliches Guthaben:</strong>{' '}
                {formatEuro(voucher.initialBalance?.sum)}
              </Typography>
              <Typography variant='body2'>
                <strong>Gültig bis:</strong>{' '}
                {voucher.expirationDate
                  ? dayjs(voucher.expirationDate).format('DD.MM.YYYY')
                  : '-'}
              </Typography>
            </Box>

            {!isProductVoucher && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
              >
                Hinweis: Die Online-Einlösung von Wertgutscheinen ist derzeit deaktiviert.
                Online-Einlösungen erscheinen hier automatisch, sobald die Funktion aktiviert
                wird.
              </Typography>
            )}
          </Paper>

          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Auf- und Entladungen
          </Typography>

          {transactions.length === 0 ? (
            <Typography color='text.secondary' sx={{ my: 2 }}>
              Keine Transaktionen vorhanden
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Datum</TableCell>
                    <TableCell>Art</TableCell>
                    <TableCell align='right'>Betrag</TableCell>
                    <TableCell align='right'>Restguthaben</TableCell>
                    <TableCell>Wofür eingelöst</TableCell>
                    <TableCell>Filiale</TableCell>
                    <TableCell>Benutzer</TableCell>
                    <TableCell align='center'>Buchung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx, index) => {
                    const isRedeem = tx.type === 'redeem';
                    const amountSum = tx.amount?.sum;
                    const signedAmount =
                      amountSum == null
                        ? '-'
                        : `${isRedeem ? '-' : '+'} ${formatEuro(amountSum)}`;
                    return (
                      <TableRow key={tx._id || index}>
                        <TableCell>
                          {tx.date ? dayjs(tx.date).format('DD.MM.YYYY HH:mm') : '-'}
                        </TableCell>
                        <TableCell>{getTransactionTypeLabel(tx.type)}</TableCell>
                        <TableCell align='right'>{signedAmount}</TableCell>
                        <TableCell align='right'>
                          {formatEuro(tx.balanceAfterTransaction?.sum)}
                        </TableCell>
                        <TableCell>{tx.comment || '-'}</TableCell>
                        <TableCell>{tx.branchLongName || '-'}</TableCell>
                        <TableCell>{tx.issuedByUsername || '-'}</TableCell>
                        <TableCell align='center'>
                          <Tooltip
                            title={
                              tx.relatedBookingId ? 'Buchung öffnen' : 'Keine Buchung'
                            }
                          >
                            <span>
                              <IconButton
                                size='small'
                                disabled={!tx.relatedBookingId}
                                onClick={() => openBooking(tx.relatedBookingId)}
                              >
                                <OpenInNewIcon fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {!loading && searched && !voucher && (
        <Typography color='text.secondary' sx={{ my: 2 }}>
          Kein Gutschein gefunden.
        </Typography>
      )}

      <BookingDetailsModal
        open={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        onUpdate={() => {}}
      />
    </Box>
  );
};

export default VoucherRedemptionHistory;
